import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from './Auth';
import { GoogleLogin } from "@react-oauth/google";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import AppleSignin from "react-apple-signin-auth";
import config from "./config";
import { useTheme } from "./ThemeContext";
import ThemeToggle from "./ThemeToggle";
import { TermsPage, TermsModal, TermsLink, Footer } from "./Terms";

// User Context for global state management
const UserContext = createContext();

export function UserProvider({ children }) {
   const [user, setUser] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [isAuthenticated, setIsAuthenticated] = useState(false);
   const [username, setUsername] = useState(null);
   const [isAdmin, setIsAdmin] = useState(false);

   // Initialize user data from localStorage on component mount
   useEffect(() => {
      const token = localStorage.getItem("token");
      const storedUsername = localStorage.getItem("username");
      const storedUserId = localStorage.getItem("userId");
      const storedIsAdmin = localStorage.getItem("isAdmin") === "true";

      if (token && storedUsername) {
         setUser({
            username: storedUsername,
            userId: storedUserId,
         });
         setUsername(storedUsername);
         setIsAuthenticated(true);
         setIsAdmin(storedIsAdmin);
      }

      setLoading(false);
   }, []);

   // Handle user login with email/password
    const login = async (email, password) => {
        try {
            setError(null);
            setLoading(true);

            console.log("Sending login request with email:", email);

            const response = await fetch("/api/public/login", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                // Make sure we're explicitly using the email field in the request
                body: JSON.stringify({
                    email: email,
                    password: password,
                }),
            });

            // Log the response status
            console.log("Login response status:", response.status);

            const data = await response.json();

            if (!response.ok) {
                console.error("Login failed:", data);
                throw new Error(data.message || data.error || "Failed to log in");
            }

            console.log("Login successful, received data:", data);

            // Save authentication data to localStorage
            localStorage.setItem("token", data.token);
            localStorage.setItem("username", data.username);
            localStorage.setItem("userId", data.userId);
            localStorage.setItem("isAdmin", data.isAdmin || false);

            // Update state
            setUser({
                username: data.username,
                userId: data.userId,
            });
            setUsername(data.username);
            setIsAuthenticated(true);
            setIsAdmin(data.isAdmin || false);

            return { success: true };
        } catch (err) {
            console.error("Login error:", err);
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

   // Handle user login with social provider
    const socialLogin = async (provider, token, userData = {}) => {
        try {
            setError(null);
            setLoading(true);

            // Determine the endpoint based on provider
            const endpoint = `/api/public/auth/${provider}`;

            // Prepare request body based on provider
            let requestBody;

            if (provider === "google") {
                requestBody = {
                    credential: token,
                    isSignUp: userData.isSignUp || false,
                };
            } else if (provider === "facebook") {
                requestBody = {
                    accessToken: token,
                    userID: userData.userID,
                    isSignUp: userData.isSignUp || false,
                };
            } else if (provider === "apple") {
                requestBody = {
                    authorization: {
                    identityToken: token,
                    code: userData.authorization?.code || "",
                    user: userData.user || {},
                    },
                    isSignUp: userData.isSignUp || false,
                };
            } else {
                // For other providers
                requestBody = {
                    token,
                    ...userData,
                };
            }

            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error || data.message || `Failed to log in with ${provider}`
                );
            }

            // Save authentication data to localStorage
            localStorage.setItem("token", data.token);
            localStorage.setItem("username", data.username);
            localStorage.setItem("userId", data.userId);
            localStorage.setItem("isAdmin", data.isAdmin || false);

            // Update state
            setUser({
                username: data.username,
                userId: data.userId,
            });
            setUsername(data.username);
            setIsAuthenticated(true);
            setIsAdmin(data.isAdmin || false);

            return { success: true };
        } catch (err) {
            console.error(`${provider} login error:`, err);
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

   // Handle user signup
   const signup = async (username, email, password) => {
      try {
         setError(null);
         setLoading(true);

         const response = await fetch("/api/public/signup", {
            method: "POST",
            headers: {
               "Content-Type": "application/json",
            },
            body: JSON.stringify({ username, email, password }),
         });

         const data = await response.json();

         if (!response.ok) {
            throw new Error(data.message || "Failed to sign up");
         }

         // If auto-login after signup is desired, set auth data here
         // For now, we'll just return success and let the login component handle it
         return { success: true };
      } catch (err) {
         setError(err.message);
         return { success: false, error: err.message };
      } finally {
         setLoading(false);
      }
   };

   // Handle user logout
   const logout = () => {
      // Clear all auth data from localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      localStorage.removeItem("userId");
      localStorage.removeItem("isAdmin");

      // Reset state
      setUser(null);
      setUsername(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
   };

   // Context value to be provided to consumers
   const value = {
      user,
      username,
      isAdmin,
      isAuthenticated,
      loading,
      error,
      login,
      socialLogin,
      signup,
      logout,
      userId: user?.userId,
   };

   return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// Custom hook for using the user context
export function useUser() {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
}

// Login Component
export function Login({ onLogin, setView }) {
   const [email, setEmail] = useState("");
   const [password, setPassword] = useState("");
   const [error, setError] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const { login, socialLogin } = useUser();
   const { theme } = useTheme(); // Get current theme
   const isLightMode = theme === "light"; // Check if we're in light mode

   const handleSubmit = async (e) => {
      e.preventDefault();

      if (!email || !password) {
         setError("Please enter both email and password");
         return;
      }

      setIsLoading(true);
      setError(null);

      try {
         // Make it clear in the console that we're using email login
         console.log("Attempting login with email:", email);

         // Send the request with the email in the right field
         const result = await login(email, password);

         if (result.success) {
            if (typeof onLogin === "function") {
               onLogin();
            }
         } else {
            setError(result.error || "Login failed. Please try again.");
         }
      } catch (err) {
         setError("An unexpected error occurred. Please try again.");
         console.error("Login error:", err);
      } finally {
         setIsLoading(false);
      }
   };

   // Social login handlers
   const handleGoogleSuccess = async (credentialResponse) => {
      try {
         setIsLoading(true);
         if (!credentialResponse.credential) {
            setError("Invalid Google response");
            return;
         }

         // Use the socialLogin method from UserProvider
         const result = await socialLogin(
            "google",
            credentialResponse.credential,
            { isSignUp: false }
         );

         if (result.success) {
            // Call the onLogin callback if successful
            if (typeof onLogin === "function") {
               onLogin();
            }
         } else {
            setError(result.error || "Google authentication failed");
         }
      } catch (error) {
         console.error("Error during Google authentication:", error);
         setError(
            error.message || "An error occurred during Google authentication"
         );
      } finally {
         setIsLoading(false);
      }
   };

   const handleGoogleError = () => {
      setError("Google sign-in was cancelled or failed");
   };

   const handleFacebookSuccess = async (response) => {
      try {
         setIsLoading(true);

         // Check if we have a valid response from Facebook
         if (!response.accessToken || !response.userID) {
            setError("Invalid Facebook response");
            return;
         }

         // Debug response from Facebook
         console.log("Facebook login response:", response);

         // Use the socialLogin method from UserProvider
         const result = await socialLogin("facebook", response.accessToken, {
            userID: response.userID,
            email: response.email,
            isSignUp: false,
         });

         if (result.success) {
            // Call the onLogin callback if successful
            if (typeof onLogin === "function") {
               onLogin();
            }
         } else {
            setError(result.error || "Facebook authentication failed");
         }
      } catch (error) {
         console.error("Error during Facebook authentication:", error);
         setError(
            error.message || "An error occurred during Facebook authentication"
         );
      } finally {
         setIsLoading(false);
      }
   };

   const handleAppleSuccess = async (response) => {
      try {
         setIsLoading(true);

         console.log("Apple sign-in response:", response);

         // Make sure we have the required credential
         if (!response.authorization || !response.authorization.id_token) {
            setError("Invalid Apple response - missing credentials");
            return;
         }

         // Use the socialLogin method from UserProvider
         const result = await socialLogin(
            "apple",
            response.authorization.id_token,
            {
               authorization: response.authorization,
               user: response.user || {}, // Apple may not always return user data
               isSignUp: false,
            }
         );

         if (result.success) {
            // Call the onLogin callback if successful
            if (typeof onLogin === "function") {
               onLogin();
            }
         } else {
            setError(result.error || "Apple authentication failed");
         }
      } catch (error) {
         console.error("Error during Apple authentication:", error);
         setError(
            error.message || "An error occurred during Apple authentication"
         );
      } finally {
         setIsLoading(false);
      }
   };

   // Reset error when inputs change
   useEffect(() => {
      if (error) setError(null);
   }, [email, password]);

   return (
      <div className="container">
         <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
               <div
                  style={{
                     backgroundColor: isLightMode
                        ? "var(--bg-elevated)"
                        : "rgba(34, 34, 34, 0.98)",
                     borderRadius: "0.5rem",
                     border: `1px solid ${
                        isLightMode ? "var(--border-color)" : "#333"
                     }`,
                     padding: "2rem",
                     boxShadow: isLightMode
                        ? "0 4px 8px rgba(0, 0, 0, 0.1)"
                        : "0 4px 8px rgba(0, 0, 0, 0.3)",
                     marginBottom: "1.5rem",
                     animation: "fadeInUp 0.5s ease-out",
                  }}
               >
                  <div className="text-center mb-4">
                     <h2
                        className="inter-font"
                        style={{
                           color: isLightMode ? "var(--text-primary)" : "#fff",
                           fontWeight: "700",
                           fontSize: "1.75rem",
                           marginBottom: "1rem",
                        }}
                     >
                        Welcome Back
                     </h2>
                     <p
                        style={{
                           color: isLightMode ? "var(--text-muted)" : "#aaa",
                           marginBottom: "1.5rem",
                        }}
                     >
                        Sign in to continue to your account
                     </p>
                  </div>

                  {error && (
                     <div
                        style={{
                           backgroundColor: isLightMode
                              ? "rgba(239, 68, 68, 0.05)"
                              : "rgba(239, 68, 68, 0.1)",
                           borderRadius: "0.5rem",
                           padding: "1rem",
                           marginBottom: "1.5rem",
                           color: "var(--accent-danger)",
                           border: isLightMode
                              ? "1px solid rgba(239, 68, 68, 0.2)"
                              : "1px solid rgba(239, 68, 68, 0.3)",
                        }}
                     >
                        {error}
                     </div>
                  )}

                  <form onSubmit={handleSubmit}>
                     <div className="mb-3">
                        <label
                           htmlFor="email"
                           style={{
                              color: isLightMode
                                 ? "var(--text-primary)"
                                 : "#fff",
                              marginBottom: "0.5rem",
                              fontWeight: "500",
                           }}
                        >
                           Email Address
                        </label>
                        <input
                           type="email"
                           className="form-control"
                           id="email"
                           value={email}
                           onChange={(e) => setEmail(e.target.value)}
                           placeholder="Enter your email"
                           required
                           style={{
                              backgroundColor: isLightMode
                                 ? "var(--input-bg)"
                                 : "#343434",
                              color: isLightMode ? "var(--input-text)" : "#FFF",
                              borderRadius: "1rem",
                              padding: "0.75rem 1rem",
                              border: `1px solid ${
                                 isLightMode ? "var(--input-border)" : "#949494"
                              }`,
                           }}
                        />
                     </div>

                     <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                           <label
                              htmlFor="password"
                              style={{
                                 color: isLightMode
                                    ? "var(--text-primary)"
                                    : "#fff",
                                 marginBottom: "0.5rem",
                                 fontWeight: "500",
                              }}
                           >
                              Password
                           </label>
                           <a
                              href="#"
                              onClick={(e) => {
                                 e.preventDefault();
                                 if (setView) setView("forgot-password");
                              }}
                              style={{
                                 color: "var(--accent-primary)",
                                 textDecoration: "none",
                                 fontSize: "0.875rem",
                                 fontWeight: "500",
                              }}
                           >
                              Forgot Password?
                           </a>
                        </div>
                        <input
                           type="password"
                           className="form-control"
                           id="password"
                           value={password}
                           onChange={(e) => setPassword(e.target.value)}
                           placeholder="Enter your password"
                           required
                           style={{
                              backgroundColor: isLightMode
                                 ? "var(--input-bg)"
                                 : "#343434",
                              color: isLightMode ? "var(--input-text)" : "#FFF",
                              borderRadius: "1rem",
                              padding: "0.75rem 1rem",
                              border: `1px solid ${
                                 isLightMode ? "var(--input-border)" : "#949494"
                              }`,
                           }}
                        />
                     </div>

                     <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                           backgroundColor: "var(--accent-primary)",
                           color: "white",
                           padding: "0.75rem 1.5rem",
                           borderRadius: "0.375rem",
                           border: "none",
                           fontSize: "0.95rem",
                           fontWeight: "500",
                           cursor: "pointer",
                           width: "100%",
                           marginBottom: "1rem",
                           transition: "background-color 0.2s ease",
                        }}
                        onMouseOver={(e) =>
                           (e.currentTarget.style.backgroundColor = "#0e9f6e")
                        }
                        onMouseOut={(e) =>
                           (e.currentTarget.style.backgroundColor =
                              "var(--accent-primary)")
                        }
                     >
                        {isLoading ? (
                           <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                           ></span>
                        ) : null}
                        {isLoading ? "Signing in..." : "Sign In"}
                     </button>
                  </form>
                  <div
                     style={{
                        position: "relative",
                        textAlign: "center",
                        margin: "1.5rem 0",
                     }}
                  >
                     <hr
                        style={{
                           border: "none",
                           borderBottom: `1px solid ${
                              isLightMode ? "var(--border-color)" : "#444"
                           }`,
                        }}
                     />
                     <span
                        style={{
                           position: "absolute",
                           top: "50%",
                           left: "50%",
                           transform: "translate(-50%, -50%)",
                           backgroundColor: isLightMode
                              ? "var(--bg-elevated)"
                              : "#222",
                           padding: "0 1rem",
                           color: isLightMode ? "var(--text-muted)" : "#aaa",
                        }}
                     >
                        OR
                     </span>
                  </div>

                  {/* Google Login */}
                  {config.socialAuth.googleClientId && (
                     <div className="mb-3">
                        <GoogleLogin
                           onSuccess={handleGoogleSuccess}
                           onError={handleGoogleError}
                           useOneTap={false}
                           theme={isLightMode ? "outline" : "filled_black"}
                           text="signin_with"
                           shape="pill"
                           width="100%"
                           disabled={isLoading}
                        />
                     </div>
                  )}

                  {/* Facebook Login */}
                  {/* {config.socialAuth.facebookAppId && (
                     <div className="mb-3">
                        <FacebookLogin
                           appId={config.socialAuth.facebookAppId}
                           autoLoad={false}
                           fields="name,email,picture"
                           callback={handleFacebookSuccess}
                           render={(renderProps) => (
                              <button
                                 onClick={renderProps.onClick}
                                 disabled={isLoading}
                                 style={{
                                    backgroundColor: "#1877F2",
                                    color: "white",
                                    padding: "0.75rem 1.5rem",
                                    borderRadius: "0.375rem",
                                    border: "none",
                                    fontSize: "0.95rem",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    width: "100%",
                                    marginBottom: "1rem",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "background-color 0.2s ease",
                                 }}
                                 onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                       "#166FE5")
                                 }
                                 onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                       "#1877F2")
                                 }
                              >
                                 <div style={{ marginRight: "10px" }}>
                                    <svg
                                       width="24px"
                                       height="24px"
                                       viewBox="126.445 2.281 589 589"
                                       xmlns="http://www.w3.org/2000/svg"
                                       fill="#000000"
                                    >
                                       <g
                                          id="SVGRepo_bgCarrier"
                                          stroke-width="0"
                                       ></g>
                                       <g
                                          id="SVGRepo_tracerCarrier"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                       ></g>
                                       <g id="SVGRepo_iconCarrier">
                                          <circle
                                             cx="420.945"
                                             cy="296.781"
                                             r="294.5"
                                             fill="#3c5a9a"
                                          ></circle>
                                          <path
                                             d="M516.704 92.677h-65.239c-38.715 0-81.777 16.283-81.777 72.402.189 19.554 0 38.281 0 59.357H324.9v71.271h46.174v205.177h84.847V294.353h56.002l5.067-70.117h-62.531s.14-31.191 0-40.249c0-22.177 23.076-20.907 24.464-20.907 10.981 0 32.332.032 37.813 0V92.677h-.032z"
                                             fill="#ffffff"
                                          ></path>
                                       </g>
                                    </svg>
                                 </div>
                                 {isLoading
                                    ? "Connecting..."
                                    : "Continue with Facebook"}
                              </button>
                           )}
                        />
                     </div>
                  )} */}

                  {/* Apple Login */}
                  {/* {config.socialAuth.appleClientId && (
                     <div className="mb-3">
                        <AppleSignin
                           authOptions={{
                              clientId: config.socialAuth.appleClientId,
                              scope: "email name",
                              redirectURI:
                                 window.location.origin +
                                 "/auth/apple/callback", // Modified URL path
                              state: "login",
                              nonce: `nonce-${Math.random()
                                 .toString(36)
                                 .substring(2, 15)}`,
                              usePopup: true,
                           }}
                           onSuccess={handleAppleSuccess}
                           onError={(error) => {
                              console.error("Apple sign-in error:", error);
                              setError("Apple sign-in was cancelled or failed");
                           }}
                           render={({ onClick }) => (
                              <button
                                 onClick={onClick}
                                 disabled={isLoading}
                                 style={{
                                    backgroundColor: isLightMode
                                       ? "#FFFFFF"
                                       : "#000",
                                    color: isLightMode ? "#000" : "white",
                                    padding: "0.75rem 1.5rem",
                                    borderRadius: "0.375rem",
                                    border: isLightMode
                                       ? "1px solid #000"
                                       : "none",
                                    fontSize: "0.95rem",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    width: "100%",
                                    marginBottom: "1rem",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "background-color 0.2s ease",
                                 }}
                                 onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                       isLightMode ? "#F9F9F9" : "#333")
                                 }
                                 onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                       isLightMode ? "#FFFFFF" : "#000")
                                 }
                              >
                                 <div style={{ marginRight: "10px" }}>
                                    <svg
                                       fill={isLightMode ? "#000" : "#FFF"}
                                       width="24px"
                                       height="24px"
                                       viewBox="-52.01 0 560.035 560.035"
                                       xmlns="http://www.w3.org/2000/svg"
                                       stroke={isLightMode ? "#000" : "#FFF"}
                                    >
                                       <g
                                          id="SVGRepo_bgCarrier"
                                          stroke-width="0"
                                       ></g>
                                       <g
                                          id="SVGRepo_tracerCarrier"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                       ></g>
                                       <g id="SVGRepo_iconCarrier">
                                          <path d="M380.844 297.529c.787 84.752 74.349 112.955 75.164 113.314-.622 1.988-11.754 40.191-38.756 79.652-23.343 34.117-47.568 68.107-85.731 68.811-37.499.691-49.557-22.236-92.429-22.236-42.859 0-56.256 21.533-91.753 22.928-36.837 1.395-64.889-36.891-88.424-70.883-48.093-69.53-84.846-196.475-35.496-282.165 24.516-42.554 68.328-69.501 115.882-70.192 36.173-.69 70.315 24.336 92.429 24.336 22.1 0 63.59-30.096 107.208-25.676 18.26.76 69.517 7.376 102.429 55.552-2.652 1.644-61.159 35.704-60.523 106.559M310.369 89.418C329.926 65.745 343.089 32.79 339.498 0 311.308 1.133 277.22 18.785 257 42.445c-18.121 20.952-33.991 54.487-29.709 86.628 31.421 2.431 63.52-15.967 83.078-39.655"></path>
                                       </g>
                                    </svg>
                                 </div>
                                 {isLoading
                                    ? "Connecting..."
                                    : "Continue with Apple"}
                              </button>
                           )}
                        />
                     </div>
                  )} */}
               </div>

               <div
                  style={{
                     backgroundColor: isLightMode
                        ? "var(--bg-elevated)"
                        : "rgba(34, 34, 34, 0.98)",
                     borderRadius: "0.5rem",
                     border: `1px solid ${
                        isLightMode ? "var(--border-color)" : "#333"
                     }`,
                     padding: "1.5rem",
                     boxShadow: isLightMode
                        ? "0 4px 8px rgba(0, 0, 0, 0.1)"
                        : "0 4px 8px rgba(0, 0, 0, 0.3)",
                     textAlign: "center",
                  }}
               >
                  <p
                     style={{
                        color: isLightMode ? "var(--text-muted)" : "#aaa",
                        margin: 0,
                     }}
                  >
                     Don't have an account?{" "}
                     <a
                        href="#"
                        onClick={(e) => {
                           e.preventDefault();
                           if (setView) setView("signup");
                        }}
                        style={{
                           color: "var(--accent-primary)",
                           textDecoration: "none",
                           fontWeight: "500",
                        }}
                     >
                        Sign Up
                     </a>
                  </p>
               </div>
            </div>
         </div>
      </div>
   );
}

// Email Verification Component
export function EmailVerification() {
    const [status, setStatus] = useState('verifying'); // verifying, success, error
    const [message, setMessage] = useState('');
    const [username, setUsername] = useState('');
    const [verificationAttempted, setVerificationAttempted] = useState(false);
    const { isAuthenticated } = useUser(); // Get authentication status

    // Get the token from URL parameters
    const location = useLocation();
    const navigate = useNavigate();

    // Extract token from URL (path or query parameters)
    const getVerificationToken = useCallback(() => {
        // First check if token is in the URL path (from the pathname)
        // URL could be like /verify-email/abc-123
        const pathParts = location.pathname.split('/');
        let token = '';

        if (pathParts.length > 2 && pathParts[1] === 'verify-email') {
            token = pathParts[2];
        } else {
            // Otherwise check query parameters
            const searchParams = new URLSearchParams(location.search);
            token = searchParams.get('token');
        }
        return token;
    }, [location]);

    // Navigate using the app's setView function
    const navigateToPublic = useCallback(() => {
        // Use the App.js exposed setView method instead of React Router navigation
        if (window.setView) {
            window.setView("public");
        } else {
            navigate('/public');
        }

        // Also update the browser URL (for consistency)
        navigate('/public', { replace: true });
    }, [navigate]);

    useEffect(() => {
        // If we've already attempted verification, don't try again
        if (verificationAttempted) {
            return;
        }

        const verifyEmail = async () => {
            // Mark that we've attempted verification
            setVerificationAttempted(true);

            // Check if user is authenticated
            if (!isAuthenticated) {
                setStatus('error');
                setMessage('You must be logged in to verify your email address. Please login and try again.');
                return;
            }

            const token = getVerificationToken();

            if (!token) {
                setStatus('error');
                setMessage('Verification token is missing. Please check your email link.');
                return;
            }

            try {
                // Get authentication token
                const authToken = localStorage.getItem('token');
                if (!authToken) {
                    setStatus('error');
                    setMessage('Authentication token not found. Please login first.');
                    return;
                }

                // Make API call to verify the token - now using the authenticated endpoint
                const res = await fetch(`/api/user/verify-email/${token}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    }
                });

                // Handle non-JSON responses (like when backend crashes)
                const contentType = res.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    throw new Error("Server returned non-JSON response");
                }

                const data = await res.json();

                if (res.ok) {
                    setStatus('success');
                    setUsername(data.username || '');
                    setMessage(data.message || 'Your email has been successfully verified!');

                    // Update local verification status
                    localStorage.setItem('emailVerified', 'true');

                    // Instead of reloading the page, navigate to home/public feed after a short delay
                    setTimeout(() => {
                        navigateToPublic();
                    }, 2000);
                } else {
                    setStatus('error');
                    setMessage(data.error || 'Verification failed. The token may be invalid or expired.');
                }
            } catch (error) {
                console.error('Verification error:', error);
                setStatus('error');
                setMessage('An error occurred during verification. Please try again later or contact support.');
            }
        };

        verifyEmail();
    }, [
        location,
        isAuthenticated,
        navigateToPublic,
        getVerificationToken,
        verificationAttempted
    ]);

    // Handle login button click
    const handleLogin = () => {
        if (window.setView) {
            window.setView("login");
        } else {
            navigate('/login');
        }
    };

    // Handle resend verification button click
    const handleResendVerification = () => {
        if (window.setView) {
            window.setView("resend-verification");
        } else {
            navigate('/resend-verification');
        }
    };

    // Handle go to public button click
    const handleGoToPublic = () => {
        navigateToPublic();
    };

    // Show login prompt if not authenticated
    if (!isAuthenticated) {
        return (
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-lg">
                            <div className="card-body p-5 text-center">
                                <div className="mb-4">
                                    <div className="display-1 text-warning">
                                        <i className="bi bi-exclamation-triangle-fill"></i>
                                    </div>
                                </div>
                                <h3 className="mb-3">Authentication Required</h3>
                                <p className="mb-4">You must be logged in to verify your email address.</p>
                                <button
                                    onClick={handleLogin}
                                    className="btn btn-primary btn-lg px-5"
                                >
                                    Login Now
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-6">
                    <div className="card border-0 shadow-lg">
                        <div className="card-body p-5 text-center">
                            {status === 'verifying' && (
                                <>
                                    <div className="mb-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                    <h3 className="mb-3">Verifying Your Email</h3>
                                    <p className="text-muted">Please wait while we verify your email address...</p>
                                </>
                            )}

                            {status === 'success' && (
                                <>
                                    <div className="mb-4">
                                        <div className="display-1 text-success">
                                            <i className="bi bi-check-circle-fill"></i>
                                        </div>
                                    </div>
                                    <h3 className="mb-3">Email Verified!</h3>
                                    <p className="mb-4">{message}</p>
                                    {username && <p className="mb-4">Welcome, <strong>{username}</strong>!</p>}
                                    <button
                                        onClick={handleGoToPublic}
                                        className="btn btn-primary btn-lg px-5"
                                    >
                                        Continue to App
                                    </button>
                                </>
                            )}

                            {status === 'error' && (
                                <>
                                    <div className="mb-4">
                                        <div className="display-1 text-danger">
                                            <i className="bi bi-x-circle-fill"></i>
                                        </div>
                                    </div>
                                    <h3 className="mb-3">Verification Failed</h3>
                                    <p className="mb-4">{message}</p>
                                    <div className="d-grid gap-2">
                                        <button
                                            onClick={handleResendVerification}
                                            className="btn btn-outline-primary"
                                        >
                                            Resend Verification Email
                                        </button>
                                        <button
                                            onClick={handleGoToPublic}
                                            className="btn btn-primary"
                                        >
                                            Go to Home Page
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


export function VerificationPending() {
    const [isSending, setIsSending] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const { username } = useUser();
    const [userEmail, setUserEmail] = useState(() => localStorage.getItem('pendingVerificationEmail') || '');
    const navigate = useNavigate();

    const handleResendVerification = async () => {
        setIsSending(true);
        setMessage("");

        try {
            // Get the token from localStorage
            const token = localStorage.getItem("token");
            if (!token) {
                setMessage("Authentication token not found. Please log in again.");
                setMessageType("danger");
                return;
            }

            // Use the authenticated endpoint
            const res = await fetch("/api/user/resend-verification", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
                // No need to send email in body as the server will get it from the authenticated user
            });

            const data = await res.json();

            if (res.ok) {
                setMessage("Verification email has been sent. Please check your inbox and spam folder.");
                setMessageType("success");
            } else {
                setMessage(data.error || "Failed to resend verification email");
                setMessageType("danger");
            }
        } catch (error) {
            console.error('Error resending verification:', error);
            setMessage("An error occurred while sending verification email");
            setMessageType("danger");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-6">
                    <div className="card border-0 shadow-lg">
                        <div className="card-header bg-success text-white text-center py-3">
                            <h3 className="m-0">Account Created Successfully!</h3>
                        </div>
                        <div className="card-body p-5">
                            <div className="text-center mb-4">
                                <div className="display-1 text-success mb-3">
                                    <i className="bi bi-check-circle"></i>
                                </div>
                                <h4 className="mb-3">Welcome, {username}!</h4>
                                <div className="alert alert-primary mb-4" role="alert">
                                    <h5 className="alert-heading">One More Step</h5>
                                    <p>We've sent a verification email to <strong>{userEmail}</strong>.</p>
                                    <hr />
                                    <p className="mb-0">Please check your inbox and click the verification link to complete your registration.</p>
                                </div>
                            </div>

                            {message && (
                                <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
                                    {message}
                                    <button type="button" className="btn-close" onClick={() => setMessage("")}></button>
                                </div>
                            )}

                            <div className="d-grid">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={handleResendVerification}
                                    disabled={isSending}
                                >
                                    {isSending ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Sending...
                                        </>
                                    ) : (
                                        'Resend Verification Email'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function AudiobookLoginPrompt({ onLogin, setView }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useUser();
    const { theme } = useTheme(); // Get current theme
    const isLightMode = theme === "light"; // Check if we're in light mode

    // We'll only check for the book, not try to save/clear it on unmount
    useEffect(() => {
        // Check if we have a book in the more persistent storage
        const savedBook = localStorage.getItem('pendingAudiobookData');
        if (savedBook) {
            console.log('AudiobookLoginPrompt: Found pending audiobook in localStorage:',
                JSON.parse(savedBook).title || 'untitled');

            // Make sure window object also has it
            try {
                window.pendingAudiobook = JSON.parse(savedBook);
            } catch (e) {
                console.error('Failed to parse saved book:', e);
            }

            // Make sure autoplay flag is set
            sessionStorage.setItem('autoPlayAfterLogin', 'true');
        } else {
            console.warn('AudiobookLoginPrompt: No pending audiobook found in localStorage');
        }

        // No cleanup function - we don't want to clear anything on unmount
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            setError("Please enter both email and password");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            console.log("Attempting login with email:", email);

            const result = await login(email, password);

            if (result.success) {
                // Double-check our persistent storage is still there
                const savedBook = localStorage.getItem('pendingAudiobookData');
                if (savedBook) {
                    console.log('Audiobook still in storage after login, ensuring autoplay flag is set');
                    sessionStorage.setItem('autoPlayAfterLogin', 'true');
                }

                // Successfully logged in, redirect to public feed
                console.log('AudiobookLoginPrompt: Redirecting to public feed for autoplay');
                onLogin();
            } else {
                setError(result.error || "Login failed. Please try again.");
            }
        } catch (err) {
            setError("An unexpected error occurred. Please try again.");
            console.error("Login error:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const goToSignup = () => {
        // Keep the pending audiobook when switching to signup
        window.loginReason = "audiobook_access";
        setView("signup");
    };

    const goToForgotPassword = () => {
        window.loginReason = "audiobook_access";
        setView("forgot-password");
    };

    return (
        <div className="container">
            <div className="row justify-content-center">
                <div className="col-md-6 col-lg-5">
                    <div
                        style={{
                            backgroundColor: isLightMode
                                ? "var(--bg-elevated)"
                                : "rgba(34, 34, 34, 0.98)",
                            borderRadius: "0.5rem",
                            border: `1px solid ${
                                isLightMode ? "var(--border-color)" : "#333"
                            }`,
                            padding: "2rem",
                            boxShadow: isLightMode
                                ? "0 4px 8px rgba(0, 0, 0, 0.1)"
                                : "0 4px 8px rgba(0, 0, 0, 0.3)",
                            marginBottom: "1.5rem",
                            animation: "fadeInUp 0.5s ease-out",
                        }}
                    >
                        <div className="text-center mb-4">
                            <div className="mb-3">
                                <svg
                                    width="64"
                                    height="64"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke={isLightMode ? "#4285F4" : "#4285F4"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                                    <circle cx="12" cy="14" r="4"></circle>
                                    <line x1="12" y1="6" x2="12" y2="6.01"></line>
                                </svg>
                            </div>
                            <h2
                                className="inter-font"
                                style={{
                                    color: isLightMode ? "var(--text-primary)" : "#fff",
                                    fontWeight: "700",
                                    fontSize: "1.75rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                Listen to Audiobooks
                            </h2>
                            <p
                                style={{
                                    color: isLightMode ? "var(--text-muted)" : "#aaa",
                                    marginBottom: "1.5rem",
                                }}
                            >
                                Sign in to continue to your account
                            </p>
                        </div>

                        {error && (
                            <div
                                style={{
                                    backgroundColor: isLightMode
                                        ? "rgba(239, 68, 68, 0.05)"
                                        : "rgba(239, 68, 68, 0.1)",
                                    borderRadius: "0.5rem",
                                    padding: "1rem",
                                    marginBottom: "1.5rem",
                                    color: "var(--accent-danger)",
                                    border: isLightMode
                                        ? "1px solid rgba(239, 68, 68, 0.2)"
                                        : "1px solid rgba(239, 68, 68, 0.3)",
                                }}
                            >
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label
                                    htmlFor="email"
                                    style={{
                                        color: isLightMode
                                            ? "var(--text-primary)"
                                            : "#fff",
                                        marginBottom: "0.5rem",
                                        fontWeight: "500",
                                    }}
                                >
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    className="form-control"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    style={{
                                        backgroundColor: isLightMode
                                            ? "var(--input-bg)"
                                            : "#343434",
                                        color: isLightMode ? "var(--input-text)" : "#FFF",
                                        borderRadius: "1rem",
                                        padding: "0.75rem 1rem",
                                        border: `1px solid ${
                                            isLightMode ? "var(--input-border)" : "#949494"
                                        }`,
                                    }}
                                />
                            </div>

                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <label
                                        htmlFor="password"
                                        style={{
                                            color: isLightMode
                                                ? "var(--text-primary)"
                                                : "#fff",
                                            marginBottom: "0.5rem",
                                            fontWeight: "500",
                                        }}
                                    >
                                        Password
                                    </label>
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            goToForgotPassword();
                                        }}
                                        style={{
                                            color: "var(--accent-primary)",
                                            textDecoration: "none",
                                            fontSize: "0.875rem",
                                            fontWeight: "500",
                                        }}
                                    >
                                        Forgot Password?
                                    </a>
                                </div>
                                <input
                                    type="password"
                                    className="form-control"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    style={{
                                        backgroundColor: isLightMode
                                            ? "var(--input-bg)"
                                            : "#343434",
                                        color: isLightMode ? "var(--input-text)" : "#FFF",
                                        borderRadius: "1rem",
                                        padding: "0.75rem 1rem",
                                        border: `1px solid ${
                                            isLightMode ? "var(--input-border)" : "#949494"
                                        }`,
                                    }}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    backgroundColor: "var(--accent-primary)",
                                    color: "white",
                                    padding: "0.75rem 1.5rem",
                                    borderRadius: "0.375rem",
                                    border: "none",
                                    fontSize: "0.95rem",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    width: "100%",
                                    marginBottom: "1rem",
                                    transition: "background-color 0.2s ease",
                                }}
                                onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#0e9f6e")
                                }
                                onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        "var(--accent-primary)")
                                }
                            >
                                {isLoading ? (
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                ) : null}
                                {isLoading ? "Signing in..." : "Sign In & Listen"}
                            </button>
                        </form>

                        {/* Features list for audiobooks */}
                        <div className="mt-4 pt-4" style={{
                            borderTop: `1px solid ${isLightMode ? "var(--border-color)" : "#333"}`,
                        }}>
                            <p className="mb-2" style={{
                                color: isLightMode ? "var(--text-primary)" : "#fff",
                                fontWeight: "500",
                            }}>
                                With your account, you can:
                            </p>
                            <ul className="ps-3" style={{
                                color: isLightMode ? "var(--text-secondary)" : "#aaa",
                            }}>
                                <li className="mb-1">Access all public audiobooks</li>
                                <li className="mb-1">Track your listening progress</li>
                                <li className="mb-1">Create your own AI audiobooks</li>
                                <li className="mb-1">Build your personal library</li>
                            </ul>
                        </div>
                    </div>

                    <div
                        style={{
                            backgroundColor: isLightMode
                                ? "var(--bg-elevated)"
                                : "rgba(34, 34, 34, 0.98)",
                            borderRadius: "0.5rem",
                            border: `1px solid ${
                                isLightMode ? "var(--border-color)" : "#333"
                            }`,
                            padding: "1.5rem",
                            boxShadow: isLightMode
                                ? "0 4px 8px rgba(0, 0, 0, 0.1)"
                                : "0 4px 8px rgba(0, 0, 0, 0.3)",
                            textAlign: "center",
                        }}
                    >
                        <p
                            style={{
                                color: isLightMode ? "var(--text-muted)" : "#aaa",
                                margin: 0,
                            }}
                        >
                            Don't have an account?{" "}
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    goToSignup();
                                }}
                                style={{
                                    color: "var(--accent-primary)",
                                    textDecoration: "none",
                                    fontWeight: "500",
                                }}
                            >
                                Sign Up
                            </a>
                        </p>

                        <button
                            className="btn btn-sm btn-outline-secondary mt-3"
                            onClick={() => {
                                console.log('Returning to public feed, clearing audiobook data');
                                delete window.loginReason;
                                delete window.pendingAudiobook;
                                localStorage.removeItem('pendingAudiobookData');
                                sessionStorage.removeItem('autoPlayAfterLogin');
                                setView("public");
                            }}
                            style={{
                                color: isLightMode ? "var(--text-muted)" : "#aaa",
                                borderColor: isLightMode ? "var(--border-color)" : "#444",
                                backgroundColor: "transparent",
                            }}
                        >
                            Return to Public Feed
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Signup Component
export function Signup({ onSignup, setView }) {
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { signup, login, socialLogin } = useUser();
    const { theme } = useTheme(); // Get current theme
    const isLightMode = theme === 'light'; // Check if we're in light mode
    const [showTermsModal, setShowTermsModal] = useState(false);


    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic form validation
        if (!username || !email || !password || !confirmPassword) {
            setError("All fields are required");
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (password.length < 8) {
            setError("Password must be at least 8 characters long");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Attempt to register the user
            const signupResult = await signup(username, email, password);

            if (signupResult.success) {
                // If registration successful, automatically log in
                const loginResult = await login(email, password);

                if (loginResult.success) {
                    if (typeof onSignup === "function") {
                        onSignup();
                    }
                } else {
                    // If login fails after signup, redirect to login page
                    if (setView) setView("login");
                }
            } else {
                setError(
                    signupResult.error || "Registration failed. Please try again."
                );
            }
        } catch (err) {
            setError("An unexpected error occurred. Please try again.");
            console.error("Signup error:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Google signup handler
    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            setIsLoading(true);
            if (!credentialResponse.credential) {
                setError("Invalid Google response");
                return;
            }

            // Use the socialLogin method from UserProvider that we already fixed
            const result = await socialLogin(
                "google",
                credentialResponse.credential,
                { isSignUp: true } // This is signup
            );

            if (result.success) {
                // Call the onSignup callback if successful
                if (typeof onSignup === "function") {
                    onSignup();
                }
            } else {
                setError(result.error || "Google signup failed");
            }
        } catch (error) {
            console.error("Error during Google authentication:", error);
            setError(
                error.message || "An error occurred during Google authentication"
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleError = () => {
        setError("Google sign-up was cancelled or failed");
    };
    
    const handleFacebookSuccess = async (response) => {
        try {
            setIsLoading(true);

            if (!response.accessToken || !response.userID) {
                setError("Invalid Facebook response");
                return;
            }

            // Use the socialLogin method for signup
            const result = await socialLogin("facebook", response.accessToken, {
                userID: response.userID,
                email: response.email,
                isSignUp: true, // This indicates it's a signup
            });

            if (result.success) {
                if (typeof onSignup === "function") {
                    onSignup();
                }
            } else {
                setError(result.error || "Facebook signup failed");
            }
        } catch (error) {
            console.error("Error during Facebook authentication:", error);
            setError(error.message || "An error occurred during Facebook signup");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAppleSignup = async (response) => {
        try {
            setIsLoading(true);

            // Make sure we have the required credential
            if (!response.authorization || !response.authorization.id_token) {
                setError("Invalid Apple response - missing credentials");
                return;
            }

            // Use the socialLogin method from UserProvider for signup
            const result = await socialLogin(
                "apple",
                response.authorization.id_token,
                {
                    authorization: response.authorization,
                    user: response.user || {}, // Apple may not always return user data
                    isSignUp: true, // This indicates it's a signup
                }
            );

            if (result.success) {
                if (typeof onSignup === "function") {
                    onSignup();
                }
            } else {
                setError(result.error || "Apple signup failed");
            }
        } catch (error) {
            console.error("Error during Apple authentication:", error);
            setError(error.message || "An error occurred during Apple signup");
        } finally {
            setIsLoading(false);
        }
    };

    // Reset error when inputs change
    useEffect(() => {
        if (error) setError(null);
    }, [username, email, password, confirmPassword]);

    return (
       <div className="container">
          <div className="row justify-content-center">
             <div className="col-md-6 col-lg-5">
                <div
                   style={{
                      backgroundColor: isLightMode
                         ? "var(--bg-elevated)"
                         : "rgba(34, 34, 34, 0.98)",
                      borderRadius: "0.5rem",
                      border: `1px solid ${
                         isLightMode ? "var(--border-color)" : "#333"
                      }`,
                      padding: "2rem",
                      boxShadow: isLightMode
                         ? "0 4px 8px rgba(0, 0, 0, 0.1)"
                         : "0 4px 8px rgba(0, 0, 0, 0.3)",
                      marginBottom: "1.5rem",
                      animation: "fadeInUp 0.5s ease-out",
                   }}
                >
                   <div className="text-center mb-4">
                      <h2
                         className="inter-font"
                         style={{
                            color: isLightMode ? "var(--text-primary)" : "#fff",
                            fontWeight: "700",
                            fontSize: "1.75rem",
                            marginBottom: "1rem",
                         }}
                      >
                         Create Your Account
                      </h2>
                      <p
                         style={{
                            color: isLightMode ? "var(--text-muted)" : "#aaa",
                            marginBottom: "1.5rem",
                         }}
                      >
                         Join our community and create your own audiobooks
                      </p>
                   </div>

                   {error && (
                      <div
                         style={{
                            backgroundColor: isLightMode
                               ? "rgba(239, 68, 68, 0.05)"
                               : "rgba(239, 68, 68, 0.1)",
                            borderRadius: "0.5rem",
                            padding: "1rem",
                            marginBottom: "1.5rem",
                            color: "var(--accent-danger)",
                            border: isLightMode
                               ? "1px solid rgba(239, 68, 68, 0.2)"
                               : "1px solid rgba(239, 68, 68, 0.3)",
                         }}
                      >
                         {error}
                      </div>
                   )}

                   <form onSubmit={handleSubmit}>
                      <div className="mb-3">
                         <label
                            htmlFor="username"
                            style={{
                               color: isLightMode
                                  ? "var(--text-primary)"
                                  : "#fff",
                               marginBottom: "0.5rem",
                               fontWeight: "500",
                            }}
                         >
                            Username
                         </label>
                         <input
                            type="text"
                            className="form-control"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Choose a username"
                            required
                            style={{
                               backgroundColor: isLightMode
                                  ? "var(--input-bg)"
                                  : "#343434",
                               color: isLightMode
                                  ? "var(--input-text)"
                                  : "#FFF",
                               borderRadius: "1rem",
                               padding: "0.75rem 1rem",
                               border: `1px solid ${
                                  isLightMode
                                     ? "var(--input-border)"
                                     : "#949494"
                               }`,
                            }}
                         />
                      </div>

                      <div className="mb-3">
                         <label
                            htmlFor="email"
                            style={{
                               color: isLightMode
                                  ? "var(--text-primary)"
                                  : "#fff",
                               marginBottom: "0.5rem",
                               fontWeight: "500",
                            }}
                         >
                            Email Address
                         </label>
                         <input
                            type="email"
                            className="form-control"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            style={{
                               backgroundColor: isLightMode
                                  ? "var(--input-bg)"
                                  : "#343434",
                               color: isLightMode
                                  ? "var(--input-text)"
                                  : "#FFF",
                               borderRadius: "1rem",
                               padding: "0.75rem 1rem",
                               border: `1px solid ${
                                  isLightMode
                                     ? "var(--input-border)"
                                     : "#949494"
                               }`,
                            }}
                         />
                      </div>

                      <div className="mb-3">
                         <label
                            htmlFor="password"
                            style={{
                               color: isLightMode
                                  ? "var(--text-primary)"
                                  : "#fff",
                               marginBottom: "0.5rem",
                               fontWeight: "500",
                            }}
                         >
                            Password
                         </label>
                         <input
                            type="password"
                            className="form-control"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Create a password"
                            required
                            style={{
                               backgroundColor: isLightMode
                                  ? "var(--input-bg)"
                                  : "#343434",
                               color: isLightMode
                                  ? "var(--input-text)"
                                  : "#FFF",
                               borderRadius: "1rem",
                               padding: "0.75rem 1rem",
                               border: `1px solid ${
                                  isLightMode
                                     ? "var(--input-border)"
                                     : "#949494"
                               }`,
                            }}
                         />
                         <small
                            style={{
                               color: isLightMode
                                  ? "var(--text-muted)"
                                  : "#aaa",
                            }}
                         >
                            Must be at least 8 characters
                         </small>
                      </div>

                      <div className="mb-4">
                         <label
                            htmlFor="confirmPassword"
                            style={{
                               color: isLightMode
                                  ? "var(--text-primary)"
                                  : "#fff",
                               marginBottom: "0.5rem",
                               fontWeight: "500",
                            }}
                         >
                            Confirm Password
                         </label>
                         <input
                            type="password"
                            className="form-control"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm your password"
                            required
                            style={{
                               backgroundColor: isLightMode
                                  ? "var(--input-bg)"
                                  : "#343434",
                               color: isLightMode
                                  ? "var(--input-text)"
                                  : "#FFF",
                               borderRadius: "1rem",
                               padding: "0.75rem 1rem",
                               border: `1px solid ${
                                  isLightMode
                                     ? "var(--input-border)"
                                     : "#949494"
                               }`,
                            }}
                         />
                      </div>

                      <TermsLink setShowTermsModal={setShowTermsModal} />

                      <button
                         type="submit"
                         disabled={isLoading}
                         style={{
                            backgroundColor: "var(--accent-primary)",
                            color: "white",
                            padding: "0.75rem 1.5rem",
                            borderRadius: "0.375rem",
                            border: "none",
                            fontSize: "0.95rem",
                            fontWeight: "500",
                            cursor: "pointer",
                            width: "100%",
                            marginBottom: "1rem",
                            transition: "background-color 0.2s ease",
                         }}
                         onMouseOver={(e) =>
                            (e.currentTarget.style.backgroundColor = "#0e9f6e")
                         }
                         onMouseOut={(e) =>
                            (e.currentTarget.style.backgroundColor =
                               "var(--accent-primary)")
                         }
                      >
                         {isLoading ? (
                            <span
                               className="spinner-border spinner-border-sm me-2"
                               role="status"
                               aria-hidden="true"
                            ></span>
                         ) : null}
                         {isLoading ? "Creating account..." : "Create Account"}
                      </button>
                   </form>

                   <div
                      style={{
                         position: "relative",
                         textAlign: "center",
                         margin: "1.5rem 0",
                      }}
                   >
                      <hr
                         style={{
                            border: "none",
                            borderBottom: `1px solid ${
                               isLightMode ? "var(--border-color)" : "#444"
                            }`,
                         }}
                      />
                      <span
                         style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            backgroundColor: isLightMode
                               ? "var(--bg-elevated)"
                               : "#222",
                            padding: "0 1rem",
                            color: isLightMode ? "var(--text-muted)" : "#aaa",
                         }}
                      >
                         OR
                      </span>
                   </div>

                   {/* Google Signup */}
                   {config.socialAuth.googleClientId && (
                      <div className="mb-3">
                         <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            useOneTap={false}
                            theme={isLightMode ? "outline" : "filled_black"}
                            text="signup_with"
                            shape="pill"
                            width="100%"
                            disabled={isLoading}
                         />
                      </div>
                   )}

                   {/* Facebook Signup */}
                   {/* {config.socialAuth.facebookAppId && (
                      <div className="mb-3">
                         <FacebookLogin
                            appId={config.socialAuth.facebookAppId}
                            autoLoad={false}
                            fields="name,email,picture"
                            callback={handleFacebookSuccess}
                            render={(renderProps) => (
                               <button
                                  onClick={renderProps.onClick}
                                  disabled={isLoading}
                                  style={{
                                     backgroundColor: "#1877F2",
                                     color: "white",
                                     padding: "0.75rem 1.5rem",
                                     borderRadius: "0.375rem",
                                     border: "none",
                                     fontSize: "0.95rem",
                                     fontWeight: "500",
                                     cursor: "pointer",
                                     width: "100%",
                                     marginBottom: "1rem",
                                     display: "flex",
                                     alignItems: "center",
                                     justifyContent: "center",
                                     transition: "background-color 0.2s ease",
                                  }}
                                  onMouseOver={(e) =>
                                     (e.currentTarget.style.backgroundColor =
                                        "#166FE5")
                                  }
                                  onMouseOut={(e) =>
                                     (e.currentTarget.style.backgroundColor =
                                        "#1877F2")
                                  }
                               >
                                  <div style={{ marginRight: "10px" }}>
                                     <svg
                                        width="24px"
                                        height="24px"
                                        viewBox="126.445 2.281 589 589"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="#000000"
                                     >
                                        <g
                                           id="SVGRepo_bgCarrier"
                                           stroke-width="0"
                                        ></g>
                                        <g
                                           id="SVGRepo_tracerCarrier"
                                           stroke-linecap="round"
                                           stroke-linejoin="round"
                                        ></g>
                                        <g id="SVGRepo_iconCarrier">
                                           <circle
                                              cx="420.945"
                                              cy="296.781"
                                              r="294.5"
                                              fill="#3c5a9a"
                                           ></circle>
                                           <path
                                              d="M516.704 92.677h-65.239c-38.715 0-81.777 16.283-81.777 72.402.189 19.554 0 38.281 0 59.357H324.9v71.271h46.174v205.177h84.847V294.353h56.002l5.067-70.117h-62.531s.14-31.191 0-40.249c0-22.177 23.076-20.907 24.464-20.907 10.981 0 32.332.032 37.813 0V92.677h-.032z"
                                              fill="#ffffff"
                                           ></path>
                                        </g>
                                     </svg>
                                  </div>
                                  {isLoading
                                     ? "Processing..."
                                     : "Sign up with Facebook"}
                               </button>
                            )}
                         />
                      </div>
                   )} */}

                   {/* Apple Signup */}
                   {/* {config.socialAuth.appleClientId && (
                      <div className="mb-3">
                         <AppleSignin
                            authOptions={{
                               clientId: config.socialAuth.appleClientId,
                               scope: "email name",
                               redirectURI:
                                  window.location.origin +
                                  "/auth/apple/callback", // Modified URL path
                               state: "signup",
                               nonce: `nonce-${Math.random()
                                  .toString(36)
                                  .substring(2, 15)}`,
                               usePopup: true,
                            }}
                            onSuccess={handleAppleSignup}
                            onError={(error) => {
                               console.error("Apple sign-in error:", error);
                               setError(
                                  "Apple sign-in was cancelled or failed"
                               );
                            }}
                            render={({ onClick }) => (
                               <button
                                  onClick={onClick}
                                  disabled={isLoading}
                                  style={{
                                     backgroundColor: isLightMode
                                        ? "#FFFFFF"
                                        : "#000",
                                     color: isLightMode ? "#000" : "white",
                                     padding: "0.75rem 1.5rem",
                                     borderRadius: "0.375rem",
                                     border: isLightMode
                                        ? "1px solid #000"
                                        : "none",
                                     fontSize: "0.95rem",
                                     fontWeight: "500",
                                     cursor: "pointer",
                                     width: "100%",
                                     marginBottom: "1rem",
                                     display: "flex",
                                     alignItems: "center",
                                     justifyContent: "center",
                                     transition: "background-color 0.2s ease",
                                  }}
                                  onMouseOver={(e) =>
                                     (e.currentTarget.style.backgroundColor =
                                        isLightMode ? "#F9F9F9" : "#333")
                                  }
                                  onMouseOut={(e) =>
                                     (e.currentTarget.style.backgroundColor =
                                        isLightMode ? "#FFFFFF" : "#000")
                                  }
                               >
                                  <div style={{ marginRight: "10px" }}>
                                     <svg
                                        fill={isLightMode ? "#000" : "#FFF"}
                                        width="24px"
                                        height="24px"
                                        viewBox="-52.01 0 560.035 560.035"
                                        xmlns="http://www.w3.org/2000/svg"
                                        stroke={isLightMode ? "#000" : "#FFF"}
                                     >
                                        <g
                                           id="SVGRepo_bgCarrier"
                                           stroke-width="0"
                                        ></g>
                                        <g
                                           id="SVGRepo_tracerCarrier"
                                           stroke-linecap="round"
                                           stroke-linejoin="round"
                                        ></g>
                                        <g id="SVGRepo_iconCarrier">
                                           <path d="M380.844 297.529c.787 84.752 74.349 112.955 75.164 113.314-.622 1.988-11.754 40.191-38.756 79.652-23.343 34.117-47.568 68.107-85.731 68.811-37.499.691-49.557-22.236-92.429-22.236-42.859 0-56.256 21.533-91.753 22.928-36.837 1.395-64.889-36.891-88.424-70.883-48.093-69.53-84.846-196.475-35.496-282.165 24.516-42.554 68.328-69.501 115.882-70.192 36.173-.69 70.315 24.336 92.429 24.336 22.1 0 63.59-30.096 107.208-25.676 18.26.76 69.517 7.376 102.429 55.552-2.652 1.644-61.159 35.704-60.523 106.559M310.369 89.418C329.926 65.745 343.089 32.79 339.498 0 311.308 1.133 277.22 18.785 257 42.445c-18.121 20.952-33.991 54.487-29.709 86.628 31.421 2.431 63.52-15.967 83.078-39.655"></path>
                                        </g>
                                     </svg>
                                  </div>
                                  {isLoading
                                     ? "Processing..."
                                     : "Sign up with Apple"}
                               </button>
                            )}
                         />
                      </div>
                   )} */}
                </div>

                <div
                   style={{
                      backgroundColor: isLightMode
                         ? "var(--bg-elevated)"
                         : "rgba(34, 34, 34, 0.98)",
                      borderRadius: "0.5rem",
                      border: `1px solid ${
                         isLightMode ? "var(--border-color)" : "#333"
                      }`,
                      padding: "1.5rem",
                      boxShadow: isLightMode
                         ? "0 4px 8px rgba(0, 0, 0, 0.1)"
                         : "0 4px 8px rgba(0, 0, 0, 0.3)",
                      textAlign: "center",
                   }}
                >
                   <p
                      style={{
                         color: isLightMode ? "var(--text-muted)" : "#aaa",
                         margin: 0,
                      }}
                   >
                      Already have an account?{" "}
                      <a
                         href="#"
                         onClick={(e) => {
                            e.preventDefault();
                            if (setView) setView("login");
                         }}
                         style={{
                            color: "var(--accent-primary)",
                            textDecoration: "none",
                            fontWeight: "500",
                         }}
                      >
                         Sign In
                      </a>
                   </p>
                </div>
             </div>
          </div>
          <TermsModal
             show={showTermsModal}
             onClose={() => setShowTermsModal(false)}
          />
       </div>
    );
}

// Forgot Password Component
export function ForgotPassword({ setView }) {
    // Get navigation options
    const navigate = typeof window.navigate === 'function' ? window.navigate : null;

    // Validate that setView is a function
    useEffect(() => {
        if (typeof setView !== 'function') {
            console.error("ForgotPassword: setView is not a function", setView);
        } else {
            console.log("ForgotPassword: setView is properly passed as a function");
        }
    }, [setView]);

    // Helper function to build the most appropriate login URL
    const getLoginUrl = () => {
        // Check if we're running in a development environment
        const isDev = window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1';

        // Check if we're on a specific subdomain
        const hasSubdomain = window.location.hostname.split('.').length > 2;

        // Base app URL
        const baseUrl = window.location.origin;

        // If we're in a single-page app setup
        if (window.location.pathname.startsWith('/app') ||
            window.location.pathname.startsWith('/dashboard')) {
            return `${baseUrl}${window.location.pathname.split('/')[1]}/login`;
        }

        // Standard case
        return `${baseUrl}/login`;
    };

    // Helper function to handle navigation
    const goToLogin = () => {
        console.log("Attempting to navigate to login from ForgotPassword");

        // Try all available navigation methods
        try {
            // First try the passed prop
            if (typeof setView === 'function') {
                console.log("Using setView prop");
                setView("login");
                return; // If this works, don't try other methods
            }

            // Then try window.setView
            if (typeof window.setView === 'function') {
                console.log("Using window.setView");
                window.setView("login");
                return; // If this works, don't try other methods
            }

            // Then try direct location change as a last resort
            const loginUrl = getLoginUrl();
            console.log("Using window.location.href as fallback, navigating to", loginUrl);
            window.location.href = loginUrl;
        } catch (error) {
            console.error("Navigation error:", error);
            // Ultimate fallback
            window.location.href = "/login";
        }
    };
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [requestSent, setRequestSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!email || !email.includes('@')) {
            setMessage("Please enter a valid email address");
            setMessageType("danger");
            return;
        }

        setIsSubmitting(true);
        setMessage("");

        try {
            const res = await fetch("/api/public/forgot-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const data = await res.json();

            if (res.ok) {
                setMessageType("success");
                setMessage(data.message || "Password reset link sent! Please check your email inbox.");
                setRequestSent(true);
                // Clear email field after successful submission
                setEmail("");
            } else {
                setMessageType("danger");
                setMessage(data.error || "Failed to process your request");
            }
        } catch (error) {
            console.error("Forgot password error:", error);
            setMessageType("danger");
            setMessage("An error occurred while processing your request");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="row justify-content-center mt-5">
            <div className="col-md-6 col-lg-5">
                <div className="card shadow-lg border-0 rounded-lg">
                    <div className="card-header bg-primary text-white d-flex align-items-center position-relative p-3">
                        <a
                            href="#"
                            className="text-white text-decoration-none position-absolute start-0 ms-3"
                            onClick={(e) => {
                                e.preventDefault();
                                goToLogin();
                            }}
                        >
                            <i className="bi bi-arrow-left me-1"></i> Back
                        </a>
                        <h3 className="w-100 text-center my-0">Forgot Password</h3>
                    </div>
                    <div className="card-body p-4">
                        <div className="text-center mb-4">
                            <p className="text-muted">Enter your email to receive a password reset link</p>
                        </div>

                        {!requestSent ? (
                            <form onSubmit={handleSubmit}>
                                <div className="form-floating mb-3">
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="email"
                                        placeholder="Email Address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isSubmitting}
                                        required
                                    />
                                    <label htmlFor="email">Email Address</label>
                                </div>
                                <div className="d-grid">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Sending...
                                            </>
                                        ) : (
                                            'Send Reset Link'
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="text-center">
                                <div className="mb-4">
                                    <div className="display-1 text-success">
                                        <i className="bi bi-envelope-check"></i>
                                    </div>
                                </div>
                                <h4 className="mb-3">Check Your Email</h4>
                                <p>We've sent instructions to reset your password. Please check your inbox.</p>
                                <div className="d-grid gap-2 mt-4">
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => setRequestSent(false)}
                                    >
                                        Try Different Email
                                    </button>
                                    <button
                                        className="btn btn-link"
                                        onClick={goToLogin}
                                    >
                                        Return to Login
                                    </button>
                                </div>
                            </div>
                        )}

                        {message && !requestSent && (
                            <div className={`alert alert-${messageType} mt-3 mb-0`}>
                                {message}
                            </div>
                        )}
                    </div>
                    <div className="card-footer text-center py-3 bg-light">
                        <div className="small">
                            Remembered your password? <button className="btn btn-link p-0" onClick={goToLogin}>Sign in</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Reset Password Component
export function ResetPassword({ setView }) {
    // Get navigation options
    const navigate = typeof window.navigate === 'function' ? window.navigate : null;

    // Validate that setView is a function
    useEffect(() => {
        if (typeof setView !== 'function') {
            console.error("ResetPassword: setView is not a function", setView);
        } else {
            console.log("ResetPassword: setView is properly passed as a function");
        }
    }, [setView]);

    // Helper function to build the most appropriate login URL
    const getLoginUrl = () => {
        // Check if we're running in a development environment
        const isDev = window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1';

        // Check if we're on a specific subdomain
        const hasSubdomain = window.location.hostname.split('.').length > 2;

        // Base app URL
        const baseUrl = window.location.origin;

        // If we're in a single-page app setup
        if (window.location.pathname.startsWith('/app') ||
            window.location.pathname.startsWith('/dashboard')) {
            return `${baseUrl}${window.location.pathname.split('/')[1]}/login`;
        }

        // Standard case
        return `${baseUrl}/login`;
    };

    // Helper function to handle navigation
    const goToLogin = () => {
        console.log("Attempting to navigate to login");

        // Try all available navigation methods
        try {
            // First try the passed prop
            if (typeof setView === 'function') {
                console.log("Using setView prop");
                setView("login");
                return; // If this works, don't try other methods
            }

            // Then try window.setView
            if (typeof window.setView === 'function') {
                console.log("Using window.setView");
                window.setView("login");
                return; // If this works, don't try other methods
            }

            // Then try direct location change as a last resort
            const loginUrl = getLoginUrl();
            console.log("Using window.location.href as fallback, navigating to", loginUrl);
            window.location.href = loginUrl;
        } catch (error) {
            console.error("Navigation error:", error);
            // Ultimate fallback
            window.location.href = "/login";
        }
    };
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const [username, setUsername] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [token, setToken] = useState("");

    // Get token from URL on component mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');

        if (urlToken) {
            setToken(urlToken);
        } else {
            // No token in URL
            setMessage("Reset token is missing. Please check your reset link.");
            setMessageType("danger");
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (newPassword.length < 6) {
            setMessage("Password must be at least 6 characters long");
            setMessageType("danger");
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage("Passwords don't match");
            setMessageType("danger");
            return;
        }

        setIsSubmitting(true);
        setMessage("");

        try {
            const res = await fetch("/api/public/reset-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    token: token,
                    new_password: newPassword
                }),
            });

            const data = await res.json();

            if (res.ok) {
                setMessageType("success");
                setMessage(data.message || "Your password has been reset successfully!");
                setResetSuccess(true);
                if (data.username) {
                    setUsername(data.username);
                }
            } else {
                setMessageType("danger");
                setMessage(data.error || "Failed to reset password");
            }
        } catch (error) {
            console.error("Password reset error:", error);
            setMessageType("danger");
            setMessage("An error occurred while resetting your password");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="row justify-content-center mt-5">
            <div className="col-md-6 col-lg-5">
                <div className="card shadow-lg border-0 rounded-lg">
                    <div className="card-header bg-primary text-white text-center">
                        <h3 className="my-2">Reset Password</h3>
                    </div>
                    <div className="card-body p-4">
                        {!token ? (
                            <div className="alert alert-danger">
                                Reset token is missing. Please make sure you clicked the correct link from your email.
                            </div>
                        ) : resetSuccess ? (
                            <div className="text-center">
                                <div className="mb-4">
                                    <div className="display-1 text-success">
                                        <i className="bi bi-check-circle-fill"></i>
                                    </div>
                                </div>
                                <h4 className="mb-3">Password Reset Successful!</h4>
                                <p>
                                    {username ? `Your password for ${username} has been reset successfully.` :
                                        'Your password has been reset successfully.'}
                                </p>
                                <div className="d-grid mt-4">
                                    <button
                                        className="btn btn-primary btn-lg"
                                        onClick={goToLogin}
                                    >
                                        Go to Login
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="text-center mb-4">
                                    <p className="text-muted">Enter your new password</p>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="newPassword" className="form-label">New Password</label>
                                        <div className="input-group">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className="form-control"
                                                id="newPassword"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                disabled={isSubmitting}
                                                required
                                                minLength="6"
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? "Hide" : "Show"}
                                            </button>
                                        </div>
                                        <div className="form-text">Password must be at least 6 characters long</div>
                                    </div>
                                    <div className="mb-4">
                                        <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            className="form-control"
                                            id="confirmPassword"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            disabled={isSubmitting}
                                            required
                                        />
                                    </div>
                                    <div className="d-grid">
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-lg"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Resetting...
                                                </>
                                            ) : (
                                                'Reset Password'
                                            )}
                                        </button>
                                    </div>
                                </form>
                                {message && (
                                    <div className={`alert alert-${messageType} mt-3 mb-0`}>
                                        {message}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// User Account Component
export function UserAccount() {
   const [user, setUser] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [successMessage, setSuccessMessage] = useState("");

   // Password state (for security tab)
   const [newPassword, setNewPassword] = useState("");
   const [confirmPassword, setConfirmPassword] = useState("");
   const [passwordError, setPasswordError] = useState("");
   const [showPassword, setShowPassword] = useState(false);
   const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

   const [isLoggingOutDevices, setIsLoggingOutDevices] = useState(false);
   const { logout } = useUser();

   // Account activity
   const [accountActivity, setAccountActivity] = useState({
      lastLogin: "Never",
      passwordLastChanged: "Never",
   });

   // Track active tab
   const [activeTab, setActiveTab] = useState("general");

   // Get user context and theme context
   const { username, isAdmin } = useUser();
   const { theme, toggleTheme } = useTheme();
   const isLightMode = theme === "light";

   // Fetch user data on component mount
   useEffect(() => {
      const fetchUserData = async () => {
         setLoading(true);
         try {
            const token = localStorage.getItem("token");
            if (!token) {
               throw new Error("No authentication token found");
            }

            const res = await fetch("/api/user/profile", {
               headers: { Authorization: `Bearer ${token}` },
            });

            if (!res.ok) {
               const data = await res.json();
               throw new Error(data.error || "Failed to fetch user data");
            }

            const userData = await res.json();
            setUser(userData);

            // Format dates for display if they exist
            const lastLogin = userData.last_login
               ? new Date(userData.last_login).toLocaleString()
               : "Never";
            const passwordChanged = userData.password_last_changed
               ? new Date(userData.password_last_changed).toLocaleString()
               : "Never";

            setAccountActivity({
               lastLogin,
               passwordLastChanged: passwordChanged,
            });
         } catch (err) {
            console.error("Error fetching user data:", err);
            setError(err.message || "Failed to fetch user data");
         } finally {
            setLoading(false);
         }
      };

      fetchUserData();
   }, []);

   // Handle password update
   const handlePasswordUpdate = async (e) => {
      e.preventDefault();

      // Reset messages
      setSuccessMessage("");
      setPasswordError("");

      // Validate password
      if (newPassword.length < 6) {
         setPasswordError("Password must be at least 6 characters long");
         return;
      }

      if (newPassword !== confirmPassword) {
         setPasswordError("Passwords don't match");
         return;
      }

      setIsUpdatingPassword(true);

      try {
         const token = localStorage.getItem("token");
         const res = await fetch("/api/user/profile", {
            method: "PUT",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ password: newPassword }),
         });

         const data = await res.json();

         if (res.ok) {
            setSuccessMessage("Password updated successfully");
            setNewPassword("");
            setConfirmPassword("");

            // Update the password last changed date
            setAccountActivity((prev) => ({
               ...prev,
               passwordLastChanged: new Date().toLocaleString(),
            }));
         } else {
            setPasswordError(data.error || "Failed to update password");
         }
      } catch (err) {
         console.error("Error updating password:", err);
         setPasswordError("An error occurred while updating your password");
      } finally {
         setIsUpdatingPassword(false);
      }
   };

   const handleLogoutAllDevices = async () => {
      if (
         window.confirm(
            "Are you sure you want to logout from all devices? You will be logged out immediately."
         )
      ) {
         setIsLoggingOutDevices(true);

         try {
            const token = localStorage.getItem("token");
            const res = await fetch("/api/user/logout-all-devices", {
               method: "POST",
               headers: {
                  Authorization: `Bearer ${token}`,
               },
            });

            // Whether it succeeds or fails, we'll logout locally
            logout();

            // Redirect to login page
            window.location.href = "/"; // Adjust if your login page has a different path
         } catch (err) {
            console.error("Error logging out all devices:", err);
            // Even if the request fails, we should still logout locally
            logout();
            window.location.href = "/";
         }
      }
   };

   if (loading) {
      return (
         <div className="container mt-5">
            <div className="text-center">
               <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
               </div>
               <p className="mt-2" style={{ color: "var(--text-primary)" }}>
                  Loading your account information...
               </p>
            </div>
         </div>
      );
   }

   return (
      <div className="container py-4">
         <div className="row">
            {/* Left sidebar with profile and navigation */}
            <div className="col-md-3">
               <div
                  className="card border-0 mb-4"
                  style={{
                     backgroundColor: "var(--bg-elevated)",
                     borderColor: "var(--border-color)",
                     boxShadow: isLightMode
                        ? "0 2px 8px rgba(0, 0, 0, 0.05)"
                        : "0 2px 8px rgba(0, 0, 0, 0.2)",
                  }}
               >
                  <div className="card-body text-center">
                     {/* Profile avatar */}
                     <div className="mb-3 position-relative">
                        <div
                           className="d-inline-flex align-items-center justify-content-center bg-primary text-white rounded-circle"
                           style={{
                              width: "100px",
                              height: "100px",
                              fontSize: "2.5rem",
                           }}
                        >
                           {username?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                     </div>
                     <h5
                        className="mb-2"
                        style={{ color: "var(--text-primary)" }}
                     >
                        {username}
                     </h5>
                     {isAdmin && (
                        <div className="badge bg-danger">Administrator</div>
                     )}
                  </div>
               </div>

               {/* Navigation menu */}
               <div className="list-group">
                  <a
                     href="#"
                     className={`list-group-item list-group-item-action border-0 ${
                        activeTab === "general"
                           ? "active bg-primary text-white"
                           : ""
                     }`}
                     onClick={(e) => {
                        e.preventDefault();
                        setActiveTab("general");
                     }}
                     style={
                        activeTab !== "general"
                           ? {
                                backgroundColor: "var(--bg-elevated)",
                                color: "var(--text-primary)",
                             }
                           : {}
                     }
                  >
                     General Information
                  </a>
                  <a
                     href="#"
                     className={`list-group-item list-group-item-action border-0 ${
                        activeTab === "security"
                           ? "active bg-primary text-white"
                           : ""
                     }`}
                     onClick={(e) => {
                        e.preventDefault();
                        setActiveTab("security");
                     }}
                     style={
                        activeTab !== "security"
                           ? {
                                backgroundColor: "var(--bg-elevated)",
                                color: "var(--text-primary)",
                             }
                           : {}
                     }
                  >
                     Security Settings
                  </a>
               </div>
            </div>

            {/* Main content area */}
            <div className="col-md-9">
               {/* Error message display */}
               {error && (
                  <div
                     className="alert alert-danger alert-dismissible fade show"
                     role="alert"
                  >
                     {error}
                     <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError("")}
                     ></button>
                  </div>
               )}

               {/* Success message display */}
               {successMessage && (
                  <div
                     className="alert alert-success alert-dismissible fade show"
                     role="alert"
                  >
                     {successMessage}
                     <button
                        type="button"
                        className="btn-close"
                        onClick={() => setSuccessMessage("")}
                     ></button>
                  </div>
               )}

               {/* Content panels for different tabs */}
               <div
                  className="card border-0"
                  style={{
                     backgroundColor: "var(--bg-elevated)",
                     borderColor: "var(--border-color)",
                     boxShadow: isLightMode
                        ? "0 2px 8px rgba(0, 0, 0, 0.05)"
                        : "0 2px 8px rgba(0, 0, 0, 0.2)",
                  }}
               >
                  {/* General Information Tab */}
                  {activeTab === "general" && (
                     <div className="card-body">
                        <h4
                           className="mb-4"
                           style={{ color: "var(--text-primary)" }}
                        >
                           Account Information
                        </h4>

                        <div className="row mb-4">
                           <div className="col-md-6">
                              <div className="mb-3">
                                 <label
                                    className="form-label"
                                    style={{ color: "var(--text-secondary)" }}
                                 >
                                    Username
                                 </label>
                                 <div
                                    className="p-3"
                                    style={{
                                       backgroundColor: "var(--bg-tertiary)",
                                       color: "var(--text-primary)",
                                       border: "1px solid var(--border-color)",
                                       borderRadius: "0.375rem",
                                       fontWeight: "500",
                                    }}
                                 >
                                    {username}
                                 </div>
                              </div>
                           </div>
                           <div className="col-md-6">
                              <div className="mb-3">
                                 <label
                                    className="form-label"
                                    style={{ color: "var(--text-secondary)" }}
                                 >
                                    Account Type
                                 </label>
                                 <div
                                    className="p-3"
                                    style={{
                                       backgroundColor: "var(--bg-tertiary)",
                                       color: "var(--text-primary)",
                                       border: "1px solid var(--border-color)",
                                       borderRadius: "0.375rem",
                                       fontWeight: "500",
                                    }}
                                 >
                                    {isAdmin ? "Administrator" : "User"}
                                 </div>
                              </div>
                           </div>
                        </div>

                        <div className="mb-4">
                           <h5 style={{ color: "var(--text-primary)" }}>
                              Theme Settings
                           </h5>
                           <div className="d-flex align-items-center mt-3">
                              <label
                                 className="form-label me-3 mb-0"
                                 style={{ color: "var(--text-primary)" }}
                              >
                                 {isLightMode ? "Light Mode" : "Dark Mode"}
                              </label>
                              <ThemeToggle />
                           </div>
                           <p
                              className="form-text mt-2"
                              style={{ color: "var(--text-muted)" }}
                           >
                              Switch between light and dark mode according to
                              your preference.
                           </p>
                        </div>
                     </div>
                  )}

                  {/* Security Settings Tab */}
                  {activeTab === "security" && (
                     <div className="card-body">
                        <h4
                           className="mb-4"
                           style={{ color: "var(--text-primary)" }}
                        >
                           Change Password
                        </h4>

                        <form onSubmit={handlePasswordUpdate}>
                           <div className="mb-3">
                              <label
                                 htmlFor="newPassword"
                                 className="form-label"
                                 style={{ color: "var(--text-secondary)" }}
                              >
                                 New Password
                              </label>
                              <div className="d-flex">
                                 <input
                                    type={showPassword ? "text" : "password"}
                                    className={`form-control ${
                                       passwordError ? "is-invalid" : ""
                                    }`}
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) =>
                                       setNewPassword(e.target.value)
                                    }
                                    disabled={isUpdatingPassword}
                                    required
                                    placeholder="Enter new password"
                                    style={{
                                       backgroundColor: "var(--input-bg)",
                                       color: "var(--input-text)",
                                       border: "1px solid var(--input-border)",
                                    }}
                                 />
                                 <button
                                    className="btn btn-outline-secondary ms-3"
                                    type="button"
                                    onClick={() =>
                                       setShowPassword(!showPassword)
                                    }
                                    style={{
                                       backgroundColor: "var(--bg-tertiary)",
                                       color: "var(--text-primary)",
                                       borderColor: "var(--border-color)",
                                       minWidth: "70px",
                                    }}
                                 >
                                    {showPassword ? "Hide" : "Show"}
                                 </button>
                              </div>
                              <div
                                 className="form-text"
                                 style={{ color: "var(--text-muted)" }}
                              >
                                 Password must be at least 6 characters long
                              </div>
                           </div>

                           <div className="mb-4">
                              <label
                                 htmlFor="confirmPassword"
                                 className="form-label"
                                 style={{ color: "var(--text-secondary)" }}
                              >
                                 Confirm Password
                              </label>
                              <div className="d-flex">
                                 <input
                                    type={showPassword ? "text" : "password"}
                                    className={`form-control ${
                                       passwordError ? "is-invalid" : ""
                                    }`}
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) =>
                                       setConfirmPassword(e.target.value)
                                    }
                                    disabled={isUpdatingPassword}
                                    required
                                    placeholder="Confirm new password"
                                    style={{
                                       backgroundColor: "var(--input-bg)",
                                       color: "var(--input-text)",
                                       border: "1px solid var(--input-border)",
                                    }}
                                 />
                                 <button
                                    className="btn btn-outline-secondary ms-3"
                                    type="button"
                                    onClick={() =>
                                       setShowPassword(!showPassword)
                                    }
                                    style={{
                                       backgroundColor: "var(--bg-tertiary)",
                                       color: "var(--text-primary)",
                                       borderColor: "var(--border-color)",
                                       minWidth: "70px",
                                    }}
                                 >
                                    {showPassword ? "Hide" : "Show"}
                                 </button>
                              </div>
                              {passwordError && (
                                 <div className="invalid-feedback d-block">
                                    {passwordError}
                                 </div>
                              )}
                           </div>

                           <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={isUpdatingPassword}
                           >
                              {isUpdatingPassword ? (
                                 <>
                                    <span
                                       className="spinner-border spinner-border-sm me-2"
                                       role="status"
                                       aria-hidden="true"
                                    ></span>
                                    Updating...
                                 </>
                              ) : (
                                 "Update Password"
                              )}
                           </button>
                        </form>

                        <h4
                           className="mb-3 mt-5"
                           style={{ color: "var(--text-primary)" }}
                        >
                           Active Sessions
                        </h4>
                        <p
                           style={{ color: "var(--text-secondary)" }}
                           className="mb-3"
                        >
                           Logging out from all devices will terminate all
                           active sessions including this one. You'll need to
                           log in again on all devices after this action.
                        </p>

                        <button
                           type="button"
                           className="btn btn-danger"
                           onClick={handleLogoutAllDevices}
                           disabled={isLoggingOutDevices}
                        >
                           {isLoggingOutDevices ? (
                              <>
                                 <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                 ></span>
                                 Processing...
                              </>
                           ) : (
                              "Logout from all devices"
                           )}
                        </button>
                     </div>
                  )}
               </div>
            </div>
         </div>
      </div>
   );
}